export const MODEL_CATEGORY_RANGES = {
  small: { min: 1, max: 30, label: 'Small' },
  medium: { min: 31, max: 60, label: 'Medium' },
  large: { min: 61, max: 90, label: 'Large' },
  pen: { min: 101, max: 200, label: 'Pen/Artistic' },
};

export const VALID_SIZE_CATEGORIES = Object.keys(MODEL_CATEGORY_RANGES);

const MODEL_PREFIX_BY_SIZE = {
  small: 'S',
  medium: 'M',
  large: 'L',
  pen: 'P',
};

export function getModelPrefix(size) {
  return MODEL_PREFIX_BY_SIZE[size?.toLowerCase()] || '';
}

export function getPrefixedCategoryRange(size) {
  const range = MODEL_CATEGORY_RANGES[size?.toLowerCase()];
  if (!range) return '';
  const prefix = getModelPrefix(size);
  return `${prefix}${range.min}-${prefix}${range.max}`;
}

export function formatModelCode(size, modelNumber) {
  const num = Number(modelNumber);
  if (Number.isNaN(num)) return String(modelNumber ?? '');
  const prefix = getModelPrefix(size);
  if (prefix) return `${prefix}${num}`;
  return formatModelCodeFromNumber(modelNumber);
}

export function formatModelCodeFromNumber(modelNumber) {
  const str = String(modelNumber ?? '').trim();
  if (/^[SMLP]\d+$/i.test(str)) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  if (num >= 1 && num <= 30) return `S${num}`;
  if (num >= 31 && num <= 60) return `M${num}`;
  if (num >= 61 && num <= 90) return `L${num}`;
  if (num >= 101 && num <= 200) return `P${num}`;
  return str;
}

export function formatModelCodeForDisplay(modelNumber, size) {
  const str = String(modelNumber ?? '').trim();
  if (/^[SMLP]\d+$/i.test(str)) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  if (size) {
    return formatModelCode(size, modelNumber);
  }
  return formatModelCodeFromNumber(modelNumber);
}

export function parseModelCodeQuery(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return { prefix: null, digits: '' };
  const match = q.match(/^([smlp])(\d*)$/);
  if (match) {
    return { prefix: match[1].toUpperCase(), digits: match[2] };
  }
  const digitsOnly = q.replace(/\D/g, '');
  return { prefix: null, digits: digitsOnly || q };
}

export function modelMatchesCodeSearch(size, modelNumber, parsed) {
  if (!parsed || (!parsed.digits && !parsed.prefix)) return true;
  if (parsed.prefix) {
    const expectedPrefix = getModelPrefix(size);
    if (parsed.prefix !== expectedPrefix) return false;
  }
  const numStr = String(modelNumber ?? '');
  const code = formatModelCode(size, modelNumber).toLowerCase();
  if (parsed.digits) {
    return numStr.includes(parsed.digits) || code.includes(`${(parsed.prefix || '').toLowerCase()}${parsed.digits}`);
  }
  return code.startsWith(parsed.prefix.toLowerCase());
}

export function isValidModelNumberForSize(modelNumber, size) {
  const range = MODEL_CATEGORY_RANGES[size?.toLowerCase()];
  if (!range || typeof modelNumber !== 'number' || Number.isNaN(modelNumber)) {
    return false;
  }
  return modelNumber >= range.min && modelNumber <= range.max;
}

export function formatCategoryRangesMessage() {
  return Object.entries(MODEL_CATEGORY_RANGES)
    .map(([size]) => {
      const label = size.charAt(0).toUpperCase() + size.slice(1);
      return `${label}: ${getPrefixedCategoryRange(size)}`;
    })
    .join(', ');
}

export function getCategoryOptionLabel(size) {
  const range = MODEL_CATEGORY_RANGES[size];
  if (!range) return '';
  return `${range.label} (Model numbers: ${getPrefixedCategoryRange(size)})`;
}

export function getAvailableModelNumbers(size, usedNumbers, excludeNumber = null, rangeOverride = null) {
  const range = rangeOverride || MODEL_CATEGORY_RANGES[size?.toLowerCase()];
  if (!range) return [];

  const exclude = excludeNumber != null && excludeNumber !== '' ? Number(excludeNumber) : null;
  const used = new Set(
    (usedNumbers || [])
      .map((n) => Number(n))
      .filter(
        (n) =>
          !Number.isNaN(n) &&
          n !== exclude &&
          n >= range.min &&
          n <= range.max
      )
  );

  const available = [];
  for (let n = range.min; n <= range.max; n++) {
    if (!used.has(n)) {
      available.push(n);
    }
  }

  if (exclude != null && !Number.isNaN(exclude) && exclude >= range.min && exclude <= range.max) {
    if (!available.includes(exclude)) {
      available.push(exclude);
      available.sort((a, b) => a - b);
    }
  }

  return available;
}

export function getCategorySlotCount(size) {
  const range = MODEL_CATEGORY_RANGES[size?.toLowerCase()];
  if (!range) return 0;
  return range.max - range.min + 1;
}
